/**
 * ENTITLEMENT (B1) — TS mirror of the Rust engine
 * (`packages/core-rust/src/use_cases/entitlement/policy.rs`).
 *
 * The **server is the authority**: whoever decides whether a student can play
 * a recording, join the class, or download the certificate is the REST gate,
 * not this. This mirror covers exactly three things:
 *
 *   • the VOCABULARY (restrictions + presets) painted on the admin screen;
 *   • the cascade RESOLUTION, to preview the effect of a change without
 *     hitting the server, and for the TS≡Rust parity test;
 *   • the explainability texts the student sees ("Blocked by your cohort's
 *     IA-2026-1 policy").
 *
 * Keep IN SYNC with the Rust: a new restriction is added in both, and the
 * parity test fails if they diverge.
 */

// ── Restrictions ────────────────────────────────────────────────────────────

/**
 * Each restriction is a named gate point.
 *
 * What does NOT exist on purpose: `grades`. The academic record (grades and
 * attendance) **is not withheld for debt** — it has legal edges and is,
 * on top of that, the lever with the most noise and the least effect.
 */
export const RESTRICTIONS = [
  "new_content",
  "all_content",
  "recordings",
  "live_join",
  "certificate",
  "submissions",
  "forum",
] as const;

export type Restriction = (typeof RESTRICTIONS)[number];

export function isRestriction(value: string): value is Restriction {
  return (RESTRICTIONS as readonly string[]).includes(value);
}

/** Discards unknown keys: what this binary doesn't know how to apply doesn't block. */
export function parseRestrictions(raw: unknown): Restriction[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = raw.filter((x): x is Restriction => typeof x === "string" && isRestriction(x));
  return [...new Set(out)].toSorted();
}

// ── Cascade levels ──────────────────────────────────────────────────────────

/** From LEAST to MOST specific. The array's order IS the specificity. */
export const POLICY_LEVELS = ["platform", "org", "program", "course", "cohort", "student"] as const;

export type PolicyLevel = (typeof POLICY_LEVELS)[number];

export function levelRank(level: PolicyLevel): number {
  return POLICY_LEVELS.indexOf(level);
}

export function isPolicyLevel(value: string): value is PolicyLevel {
  return (POLICY_LEVELS as readonly string[]).includes(value);
}

/** Readable source level; unknown falls back to `platform` (nobody decided). */
export function toPolicyLevel(value: string | null | undefined): PolicyLevel {
  return value && isPolicyLevel(value) ? value : "platform";
}

export type GrantKind = "exception" | "override";

// ── Presets ─────────────────────────────────────────────────────────────────

export interface PolicySpec {
  preset: string;
  graceDays: number;
  minDebtUsdMinor: number;
  pendingPaymentHoldsDays: number;
  restrictions: Restriction[];
  locked: boolean;
}

export interface Preset {
  key: string;
  graceDays: number;
  minDebtUsdMinor: number;
  pendingPaymentHoldsDays: number;
  restrictions: Restriction[];
}

const preset = (
  key: string,
  restrictions: Restriction[],
  graceDays = 7,
  minDebtUsdMinor = 100,
  pendingPaymentHoldsDays = 5,
): Preset => ({ key, restrictions, graceDays, minDebtUsdMinor, pendingPaymentHoldsDays });

/** Preset registry (== Rust `policy::PRESETS`, same order). */
export const PRESETS: Preset[] = [
  preset("no_block", [], 0, 0, 0),
  // The RECOMMENDED preset: most commercial leverage per unit of friction. You
  // don't advance, don't join today's class, and don't get certified — all three
  // are server-side and therefore REALLY revocable — but you keep what you paid.
  preset("recommended", ["certificate", "live_join", "new_content"]),
  preset("read_only", ["forum", "submissions"]),
  preset("block_new_content", ["live_join", "new_content"]),
  preset("block_certificate_only", ["certificate"]),
  // Everything. **Except grades and attendance**.
  preset("block_all", [
    "all_content",
    "certificate",
    "forum",
    "live_join",
    "recordings",
    "submissions",
  ]),
];

export const RECOMMENDED_PRESET_KEY = "recommended";

export function presetByKey(key: string): Preset | undefined {
  return PRESETS.find((p) => p.key === key);
}

export function specFromPreset(p: Preset): PolicySpec {
  return {
    preset: p.key,
    graceDays: p.graceDays,
    minDebtUsdMinor: p.minDebtUsdMinor,
    pendingPaymentHoldsDays: p.pendingPaymentHoldsDays,
    restrictions: p.restrictions.toSorted(),
    locked: false,
  };
}

/**
 * FACTORY default: fail open.
 *
 * Entitlement is not a confidentiality boundary — the content is sold,
 * not secret — and the cost asymmetry runs in a single direction: blocking
 * a student who did pay costs money, support, and reputation; not blocking a
 * debtor leaves the debt intact and collections keeps chasing them.
 */
export function factorySpec(): PolicySpec {
  return specFromPreset(PRESETS[0]!);
}

// ── Cascade resolution ──────────────────────────────────────────────────────

export interface PolicyLayer {
  level: PolicyLevel;
  id: string;
  scopeId?: string | null;
  label?: string | null;
  spec: PolicySpec;
  grantKind?: GrantKind | null;
  expiresOn?: string | null;
}

export interface EffectivePolicy {
  spec: PolicySpec;
  sourceLevel: PolicyLevel;
  sourceId?: string | null;
  sourceLabel?: string | null;
  grantKind?: GrantKind | null;
  expiresOn?: string | null;
  lockedAtLevel?: PolicyLevel | null;
  /** `false` = nobody configured anything; the factory default is used. */
  configured: boolean;
}

/**
 * The MOST specific level wins, and a `locked` policy is a CEILING that
 * overrides everything below it.
 *
 * Without the ceiling, the hierarchy has no emergency brake: the platform
 * couldn't say "no academy can block anyone" without stripping academies
 * of the rest of their configurability.
 */
export function resolveEffectivePolicy(layers: PolicyLayer[]): EffectivePolicy {
  if (layers.length === 0) {
    return {
      spec: factorySpec(),
      sourceLevel: "platform",
      sourceId: null,
      sourceLabel: null,
      grantKind: null,
      expiresOn: null,
      lockedAtLevel: null,
      configured: false,
    };
  }
  const sorted = layers.toSorted((a, b) => levelRank(a.level) - levelRank(b.level));
  // The LEAST specific `locked` wins: a platform one outranks an
  // organization one.
  const lockedLayer = sorted.find((l) => l.spec.locked);
  const winner = lockedLayer ?? sorted.at(-1)!;
  return {
    spec: winner.spec,
    sourceLevel: winner.level,
    sourceId: winner.id,
    sourceLabel: winner.label ?? null,
    grantKind: winner.grantKind ?? null,
    expiresOn: winner.expiresOn ?? null,
    lockedAtLevel: lockedLayer ? lockedLayer.level : null,
    configured: true,
  };
}

// ── Materialized state (what the client READS via sync) ─────────────────────

export type EntitlementStateValue = "entitled" | "grace" | "restricted";

export interface EntitlementStateRow {
  scopeLevel: string;
  scopeId?: string | null;
  state: EntitlementStateValue;
  restrictions: Restriction[];
  sourceLevel: PolicyLevel;
  sourceLabel?: string | null;
  grantKind?: GrantKind | null;
  reasonCode?: string | null;
  unblocksAt?: string | null;
}

/**
 * Does this state withdraw `restriction`? `all_content` implies `new_content`
 * (blocking everything includes what's new) — the implication lives in a
 * single place so that no consumer forgets it.
 */
export function blocks(row: Pick<EntitlementStateRow, "restrictions">, r: Restriction): boolean {
  if (row.restrictions.includes(r)) {
    return true;
  }
  return r === "new_content" && row.restrictions.includes("all_content");
}

/** The worst state in a list (for the academy's global banner). */
export function worstState(rows: Pick<EntitlementStateRow, "state">[]): EntitlementStateValue {
  if (rows.some((r) => r.state === "restricted")) {
    return "restricted";
  }
  if (rows.some((r) => r.state === "grace")) {
    return "grace";
  }
  return "entitled";
}
